"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import { ChevronRight, Image as ImageIcon, LayoutGrid, Hammer, ArrowLeft } from "lucide-react";
import { Card, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";

// 導入我們剛剛改好的 Props 完全體展示元件
import ShowProject from "./Showproject";
const MENU_STRUCTURE = [{
  section: "工程實績 / PROJECTS",
  icon: <LayoutGrid className="size-4" />,
  items: [{
    name: "大樓住宅 / RESIDENTIAL",
    id: 89
  }, {
    name: "透天別墅 / VILLA",
    id: 90
  }]
}, {
  section: "專業服務 / SERVICES",
  icon: <Hammer className="size-4" />,
  items: [{
    name: "商用空間 / COMMERCIAL",
    id: 91
  }, {
    name: "老屋翻修 / RENOVATION",
    id: 92
  }, {
    name: "辦公空間 / OFFICE",
    id: 93
  }, {
    name: "戶外地板 / OUTDOOR",
    id: 94
  }]
}];
interface FilterBarProps {
  activeId: number | null;
  onCategoryClick: (id: number | null) => void;
  filteredMenu: typeof MENU_STRUCTURE;
  scrollToSection: () => void;
}
function FilterBar({
  activeId,
  onCategoryClick,
  filteredMenu,
  scrollToSection
}: FilterBarProps) {
  return <div className="relative">
      <div className="absolute right-0 top-0 bottom-4 w-16 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
      <div className="grid grid-rows-2 grid-flow-col gap-y-3 gap-x-4 overflow-x-auto pb-4 scrollbar-hide relative">
        {(() => {
        const isAllActive = activeId === null || activeId === undefined;
        return <button key="all" onClick={() => {
          onCategoryClick(null);
          scrollToSection();
        }} className={cn("group flex flex-col items-center justify-center px-6 md:px-10 h-15 md:h-17 rounded-full border transition-all duration-300 ease-out cursor-pointer w-full md:w-auto md:min-w-[170px] select-none text-center shrink-0 overflow-hidden", isAllActive ? "bg-[#604f45] border-[#604f45] text-white shadow-md" : "bg-transparent border-[#604f45] text-[#604f45] hover:bg-[#604f45] hover:border-[#604f45] hover:text-white")}>
              <span className={cn("font-hk-regular text-xs md:text-sm font-medium tracking-widest transition-transform duration-300 ease-out whitespace-nowrap", !isAllActive && "group-hover:-translate-y-0.5")}>
                全部
              </span>
              <div className={cn("w-8 bg-white/40 transition-all duration-300 ease-out h-0 my-0 opacity-0 scale-50", !isAllActive && "group-hover:h-[1px] group-hover:my-1.5 group-hover:opacity-100 group-hover:scale-100")} />
              <div className={cn("font-sans text-[9px] md:text-[10px] tracking-wide font-light tracking-widest uppercase transition-all duration-300 ease-out whitespace-nowrap overflow-hidden text-center flex items-center justify-center", isAllActive ? "h-0 opacity-0 mt-0 text-transparent" : "h-0 opacity-0 mt-0 text-transparent group-hover:h-3.5 group-hover:mt-0.5 group-hover:opacity-100 group-hover:text-white/80")}>
                All
              </div>
            </button>;
      })()}

        {filteredMenu.map(group => group.items.map(cat => {
        const isActive = activeId === cat.id;
        const parts = cat.name.split("/");
        const cnText = parts[0]?.trim() || cat.name;
        const enText = parts[1]?.trim() || "";
        return <button key={cat.id} onClick={() => {
          onCategoryClick(cat.id);
          scrollToSection();
        }} className={cn("group flex flex-col items-center justify-center px-6 md:px-10 h-15 md:h-17 rounded-full border transition-all duration-300 ease-out cursor-pointer w-full md:w-auto md:min-w-[170px] select-none text-center shrink-0 overflow-hidden", isActive ? "bg-[#604f45] border-[#604f45] text-white shadow-md" : "bg-transparent border-[#604f45] text-[#604f45] hover:bg-[#604f45] hover:border-[#604f45] hover:text-white")}>
                <span className={cn("font-hk-regular text-xs md:text-sm font-medium tracking-widest transition-transform duration-300 ease-out whitespace-nowrap", !isActive && "group-hover:-translate-y-0.5")}>
                  {cnText}
                </span>
                <div className={cn("w-8 bg-white/40 transition-all duration-300 ease-out h-0 my-0 opacity-0 scale-50", !isActive && "group-hover:h-[1px] group-hover:my-1.5 group-hover:opacity-100 group-hover:scale-100")} />
                <div className={cn("font-sans text-[9px] md:text-[10px] tracking-wide font-light tracking-widest uppercase transition-all duration-300 ease-out whitespace-nowrap overflow-hidden text-center flex items-center justify-center", isActive || !enText ? "h-0 opacity-0 mt-0 text-transparent" : "h-0 opacity-0 mt-0 text-transparent group-hover:h-3.5 group-hover:mt-0.5 group-hover:opacity-100 group-hover:text-white/80")}>
                  {enText}
                </div>
              </button>;
      }))}
      </div>

      <div className="absolute right-2 top-1/2 -translate-y-1/2 md:hidden pointer-events-none z-20">
        <ChevronRight className="size-5 text-[#604f45]/40 animate-pulse" />
      </div>
    </div>;
}
export default function CaseShowcaseArea() {
  const [activeId, setActiveId] = useState<number | null>(null);
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  //  優化關鍵 1：選中的專案 ID，改為與網址 Hash 路由完全同步同步控管
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);

  // 智慧環境識別
  const apiBase = useMemo(() => {
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;
      if (hostname.includes("localhost") || hostname.includes("codesandbox") || hostname.includes("csb.app") || hostname.includes("10web.io")) {
        return "https://hents.com.tw";
      }
    }
    return "";
  }, []);

  //  優化關鍵 2：監聽瀏覽器網址 Hash 變更（支援手機滑動上一頁體驗）
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash && hash.startsWith("#project-")) {
        const projectId = Number(hash.replace("#project-", ""));
        if (!isNaN(projectId)) {
          setSelectedProjectId(projectId);
          // 頂部平滑滾動至專案細節區塊
          setTimeout(() => {
            const section = document.getElementById("project-section-top");
            if (section) section.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }, 100);
        }
      } else {
        // 當使用者按上一頁或滑動返回時，Hash 消失，這裡會自動卸載 ShowProject 返回目錄
        setSelectedProjectId(null);
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    handleHashChange(); // 頁面初始化檢測（支援直接帶 Hash 進網頁網址）

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  //  優化關鍵 3：自訂 Hash 路由開關機制
  const openProjectModal = (id: number) => {
    window.location.hash = `project-${id}`;
  };
  const closeProjectModal = () => {
    if (window.location.hash) {
      window.history.back(); // 觸發瀏覽器原生歷史紀錄返回
    } else {
      setSelectedProjectId(null);
    }
  };

  // 自主控管全動態 Fetch作品清單
  useEffect(() => {
    const fetchPortfolioList = async () => {
      try {
        setLoading(true);
        let restUrl = `${apiBase}/wp-json/wp/v2/portfolio?per_page=12&page=${currentPage}&_=${Date.now()}`;
        if (activeId) {
          restUrl += `&portfolio_category=${activeId}`;
        }
        const targetUrl = restUrl;
        const response = await fetch(targetUrl, {
          method: "GET",
          headers: {
            "X-Requested-With": "XMLHttpRequest"
          }
        });
        if (!response.ok) throw new Error("撈取作品失敗");
        const totalPagesHeader = response.headers.get("X-WP-TotalPages");
        if (totalPagesHeader) {
          setTotalPages(Number(totalPagesHeader));
        }
        const rawData = await response.json();
        const formattedPosts = rawData.map((item: any) => ({
          id: item.id,
          title: item.title?.rendered || "未命名案場",
          featuredImage: item.acf_images?.image_1 || "https://images.unsplash.com/photo-1513694203232-719a280e022f?q=80&w=1200",
          tags: item.acf?.flooring_series ? [{
            id: "series",
            name: `#${item.acf.flooring_series}`
          }] : [{
            id: "hent",
            name: "#實績案例"
          }]
        }));
        setPosts(formattedPosts);
      } catch (err) {
        console.error(" 作品清單撈取失敗：", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPortfolioList();
  }, [activeId, currentPage, apiBase]);
  const scrollToSection = () => {
    const section = document.getElementById("project-section-top");
    if (section) section.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };
  const hasPrev = currentPage > 1;
  const hasNext = currentPage < totalPages;

  // ───  視圖分流：當選中專案時，直接在畫面上方/原地切換成新頁面姿態 ───
  if (selectedProjectId) {
    return <div className="bg-background min-h-screen py-12 animate-in fade-in duration-300">
        <div className="mx-auto max-w-[1440px] px-4 lg:px-8">
          
          {/* 高質感返回按鈕 */}
          <div className="mb-8">
            <button onClick={closeProjectModal} //  改用一體化的路由退出機制
          className="group inline-flex items-center gap-2 text-sm font-sans font-medium text-[#604f45] bg-[#604f45]/5 hover:bg-[#604f45]/10 px-5 py-2.5 rounded-full transition-all cursor-pointer select-none">
              <ArrowLeft className="size-4 transition-transform group-hover:-translate-x-1" />
              返回實績目錄 / BACK TO LIST
            </button>
          </div>

          {/*  核心看點：直接把點擊拿到的 ID 當作 Props 餵進去！ */}
          <ShowProject id={selectedProjectId} />

          <div className="mt-16 flex justify-center pb-12">
            <button onClick={closeProjectModal} //  改用一體化的路由退出機制
          className="group inline-flex items-center gap-2 text-sm font-sans font-medium text-white bg-[#604f45] hover:bg-[#4A3728] px-8 py-3 rounded-full transition-all cursor-pointer shadow-md">
              <ArrowLeft className="size-4 transition-transform group-hover:-translate-x-1" />
              返回作品目錄 / EXPLORE MORE
            </button>
          </div>

        </div>
      </div>;
  }

  // ─── 原本的 12 格清單大網格 ───
  return <section id="project-showcase" className="bg-background py-24 md:py-32">
      <div className="mx-auto max-w-[1440px] px-4 lg:px-8">
        
        <div id="project-section-top" className="mb-20 flex justify-center text-center select-none">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <div className="relative inline-block px-1">
              <div className="absolute bottom-0 left-0 w-full h-[38%] bg-[#d3bfae]/40 z-0" />
              <h2 className="relative z-10 font-hk-regular text-2xl md:text-3xl font-normal tracking-widest text-[#3d3d3d]">
                看看別人的家
              </h2>
            </div>
            <span className="font-sans text-lg md:text-xl font-light text-[#d3bfae]">/</span>
            <span className="font-sans text-xs md:text-sm font-semibold tracking-[0.25em] text-[#d3bfae] uppercase pt-1">
              SPACES
            </span>
          </div>
        </div>

        <div className="mb-12 relative">
          <FilterBar activeId={activeId} onCategoryClick={id => {
          setActiveId(id);
          setCurrentPage(1);
        }} filteredMenu={MENU_STRUCTURE} scrollToSection={scrollToSection} />
        </div>

        <div className="w-full">
          {loading ? <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/3] bg-muted/40 animate-pulse rounded-[20px]" />)}
            </div> : posts.length === 0 ? <div className="flex flex-col items-center justify-center py-32 text-muted-foreground border border-dashed rounded-[20px] bg-white/30">
              <ImageIcon className="size-12 opacity-10 mb-4" />
              <p className="font-serif italic text-sm tracking-widest">目前此分類尚無作品 Cases coming soon</p>
            </div> : <>
              <div className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3 animate-in fade-in duration-500">
                {posts.map(post => <Card key={post.id} onClick={() => openProjectModal(post.id)} //  核心修改：點擊改調用 Hash 開啟機制
            className="group border border-black/[0.06] shadow-sm bg-[#ffffff] rounded-2xl md:rounded-[20px] p-0 flex flex-col h-full transition-all duration-300 hover:shadow-md overflow-hidden cursor-pointer">
                    <div className="relative overflow-hidden rounded-t-2xl md:rounded-t-[20px] rounded-b-none w-full shrink-0">
                      <AspectRatio ratio={4 / 3}>
                        <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                      </AspectRatio>
                    </div>
                    <CardHeader className="px-5 pt-5 pb-2 flex-grow">
                      <CardTitle className="line-clamp-2 font-sans text-[1.1rem] md:text-[1.2rem] font-normal leading-snug tracking-wide text-[#604f45] group-hover:text-[#745a3f] transition-colors">
                        {post.title}
                      </CardTitle>
                      <div className="flex flex-wrap gap-2 pt-3 select-none">
                        {post.tags.map(tag => <span key={tag.id} className="inline-flex items-center justify-center bg-[#b09e94]/20 text-[#604f45] text-[11px] font-sans font-medium px-3 py-1 rounded-full tracking-wider">
                            {tag.name}
                          </span>)}
                      </div>
                    </CardHeader>
                    <CardFooter className="px-5 pt-2 pb-5 border-none">
                      <span className="text-[11px] font-sans font-medium tracking-widest text-[#604f45] transition-opacity group-hover:opacity-80 flex items-center gap-1.5 uppercase">
                        閱讀實績細節 / VIEW DETAILS
                        <span className="transition-transform duration-300 group-hover:translate-x-1 text-xs pt-0.5">→</span>
                      </span>
                    </CardFooter>
                  </Card>)}
              </div>

              {/* 分頁器 */}
              {totalPages > 1 && <div className="mt-20 flex justify-center pt-12 border-t border-border">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious href="#" onClick={e => {
                    e.preventDefault();
                    if (hasPrev) {
                      setCurrentPage(p => p - 1);
                      scrollToSection();
                    }
                  }} className={!hasPrev ? "pointer-events-none opacity-20" : ""} />
                      </PaginationItem>
                      {[...Array(totalPages)].map((_, i) => <div key={i + 1}>
                          <PaginationLink href="#" onClick={e => {
                    e.preventDefault();
                    setCurrentPage(i + 1);
                    scrollToSection();
                  }} isActive={currentPage === i + 1}>
                            {i + 1}
                          </PaginationLink>
                        </div>)}
                      <PaginationItem>
                        <PaginationNext href="#" onClick={e => {
                    e.preventDefault();
                    if (hasNext) {
                      setCurrentPage(p => p + 1);
                      scrollToSection();
                    }
                  }} className={!hasNext ? "pointer-events-none opacity-20" : ""} />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>}
            </>}
        </div>

      </div>
    </section>;
}